import React, { forwardRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { Form as BSForm, Button, InputGroup } from 'react-bootstrap'
import { FaTrashCan } from 'react-icons/fa6'

import { useOnChange } from '../../hooks/useOnChange'
import { FormContextType, IFormDataVals } from '../../types'

export type FileUploadProps = {
    controlId: string
    className?: string
    required?: boolean
    disabled?: boolean
    multiple?: boolean
    accept?: string
    acceptPattern?: ( file: File ) => boolean
    maxFileSize?: number
    placeholder?: string
    onChange?: { ( oldVal?: IFormDataVals, newVal?: IFormDataVals ): void }
}

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>( ({ controlId, className, disabled, required, onChange, ...props }: FileUploadProps, ref ) => {

    const fileRef = React.useRef<HTMLInputElement>( null )
    const { form, onControlChange } = useOnChange( controlId, 'file', onChange )

    const [ isValid, setIsValid ] = React.useState<number>( 0 )

    const handleFileChange = ( ev: React.ChangeEvent<HTMLInputElement> ) => {
        if( !form ) return
        
        const fls = Array.from( ev.target.files ?? [] )
        if( fls.length == 0 ) return

        let errors = 0
        for( const f of fls ) {
            if( props.acceptPattern ) errors |= props.acceptPattern( f ) ? 0 : 1
            if( props.maxFileSize ) errors |=  f.size <= props.maxFileSize ? 0 : 2
        }

        if( errors === 0 ) onControlChange( fls )
        else { 
            if( fileRef.current ) fileRef.current.value = ''
            setIsValid( errors )
        }
    }

    const handleDelete = () => {
        if( fileRef.current ) fileRef.current.value = ''
        onControlChange( null )
    }

    return (<BSForm.Group controlId={ controlId } className={ [ 'mb-3', className ].join( ' ' ) }>
        <input type='file' 
            ref={ fileRef } 
            className='d-none' 
            multiple={ props.multiple } 
            accept={ props.accept } 
            onChange={ handleFileChange }
        />
        <InputGroup>
            <BSForm.Control 
                ref={ ref } 
                { ...props }
                type='text'
                value={ form ? form.data[ controlId ] as string : '' }
                required={ required }
                readOnly
                disabled={ disabled || form?.processing }
                isInvalid={ ( isValid != 0 || ( required && ( form?.data[ controlId ] ?? '' ) == '' ) ) && !disabled }
            />
            { form && form.data[ controlId ] !== '' && <Button onClick={ handleDelete } disabled={ disabled || form?.processing }><FaTrashCan /></Button> }
            <Button variant="primary" onClick={ () => { if( fileRef.current ) fileRef.current.click() } } disabled={ disabled || form?.processing }>
                <FormattedMessage description='Forms.FileUpload.UploadButton' defaultMessage='Upload' />
            </Button>
            <BSForm.Control.Feedback type='invalid'>
                { isValid & 1 ? <FormattedMessage description='Forms.FileUpload.InvalidFileType' defaultMessage='Invalid file type.' /> : undefined }
                { isValid & 3 ? ' & ' : undefined }
                { isValid & 2 ? <FormattedMessage description='Forms.FileUpload.InvalidFileSize' defaultMessage='Invalid file size.' /> : undefined }
            </BSForm.Control.Feedback>
        </InputGroup>
    </BSForm.Group>)
} )